import React from 'react';
import { FD120 } from '../../../services/d202Api';
import { dateToInput, inputToDate, fmt, getCategoryLabel, getMaterialTypeLabel } from '../helpers';
import LookupField from '../../../components/LookupField';

interface MasterFieldsProps {
  formData: Partial<FD120>;
  names: Record<string, string>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FD120) => FD120[keyof FD120];
  setField: (key: keyof FD120, value: FD120[keyof FD120]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
  importPurchaseOrder: (poNo: string) => Promise<void>;
  importInternalShipment: (shipmentNo: string) => Promise<void>;
}

const D202MasterFields: React.FC<MasterFieldsProps> = ({
  formData, names, isEditing, isFieldDisabled,
  getField, setField, resolveSingleName, getFieldError,
  importPurchaseOrder, importInternalShipment,
}) => {
  const E = (field: string) => {
    const err = getFieldError?.(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>{err}</span> : null;
  };

  return (
    <div className="tab1-layout">
      <div className="form-sections">

        {/* Section 1: 基本資料 */}
        <div className="form-section odd">
          <span className="section-tag">基本資料</span>
          <div className="form-grid">
            <label>繳庫單號</label>
            <input type="text" value={String(getField('fd120_01') || '')} disabled style={{ width: 120 }} readOnly data-contract-field="fd120_01" />

            <label className="required">採購單號{E('fd120_21')}</label>
            <LookupField lookupType="purchaseOrder" style={{ width: 120 }} maxLength={20}
              disabled={isFieldDisabled('fd120_21')}
              value={String(getField('fd120_21') || '')}
              onChange={v => setField('fd120_21', v)}
              onCodeConfirm={code => { resolveSingleName('purchaseOrder', 'purchaseOrder', code); void importPurchaseOrder(code); }} contractFieldId="fd120_21"
            />

            <label>內部交運單號</label>
            <LookupField lookupType="internalShipment" style={{ width: 120 }} maxLength={20}
              disabled={isFieldDisabled('fd120_46')}
              value={String(getField('fd120_46') || '')}
              onChange={v => setField('fd120_46', v)}
              onCodeConfirm={code => { resolveSingleName('internalShipment', 'internalShipment', code); void importInternalShipment(code); }} contractFieldId="fd120_46"
            />

            <label className="required">繳庫日期{E('fd120_11')}</label>
            <input
              type="date"
              value={dateToInput(formData.fd120_11)}
              onChange={e => setField('fd120_11', inputToDate(e.target.value) as FD120[keyof FD120])}
              disabled={isFieldDisabled('fd120_11')}
              style={{ width: 150 }} data-contract-field="fd120_11"
            />

            <label className="required">分包商代號{E('fd120_12')}</label>
            <div className="lookup-field">
              <LookupField lookupType="supplier" style={{ width: 100 }} maxLength={12}
                disabled={isFieldDisabled('fd120_12')}
                value={String(getField('fd120_12') || '')}
                onChange={v => setField('fd120_12', v)}
                onCodeConfirm={code => resolveSingleName('supplier', 'supplier', code)} contractFieldId="fd120_12"
              />
              <span className="lookup-name">{names.supplier || ''}</span>
            </div>

            <label className="required">繳庫庫位{E('fd120_14')}</label>
            <div className="lookup-field">
              <LookupField lookupType="warehouse" style={{ width: 80 }} maxLength={10}
                disabled={isFieldDisabled('fd120_14')}
                value={String(getField('fd120_14') || '')}
                onChange={v => setField('fd120_14', v)}
                onCodeConfirm={code => resolveSingleName('warehouse', 'warehouse', code)} contractFieldId="fd120_14"
              />
              <span className="lookup-name">{names.warehouse || ''}</span>
            </div>
          </div>
        </div>

        {/* Section 2: 部門/幣別 */}
        <div className="form-section even">
          <span className="section-tag">部門/幣別</span>
          <div className="form-grid">
            <label>部門代號</label>
            <div className="lookup-field">
              <LookupField lookupType="department" style={{ width: 80 }} maxLength={10}
                disabled={isFieldDisabled('fd120_17')}
                value={String(getField('fd120_17') || '')}
                onChange={v => setField('fd120_17', v)}
                onCodeConfirm={code => resolveSingleName('department', 'department', code)} contractFieldId="fd120_17"
              />
              <span className="lookup-name">{names.department || ''}</span>
            </div>

            <label className="required">幣別{E('fd120_18')}</label>
            <div className="lookup-field">
              <LookupField lookupType="currency" style={{ width: 60 }} maxLength={5}
                disabled={isFieldDisabled('fd120_18')}
                value={String(getField('fd120_18') || '')}
                onChange={v => setField('fd120_18', v)}
                onCodeConfirm={code => resolveSingleName('currency', 'currency', code)} contractFieldId="fd120_18"
              />
              <span className="lookup-name">{names.currency || ''}</span>
            </div>

            <label>匯率</label>
            <input
              type="number"
              value={formData.fd120_19 ?? 0}
              disabled
              readOnly
              style={{ width: 100, textAlign: 'right' }} data-contract-field="fd120_19"
            />

            <label>繳庫類別</label>
            <div className="radio-group">
              <label>
                <input type="radio" checked={formData.fd120_13 === 1} disabled data-contract-field="fd120_13" />
                正常
              </label>
              <label>
                <input type="radio" checked={formData.fd120_13 === 2} disabled data-contract-field="fd120_13" />
                異常
              </label>
            </div>

            <label>原料/半成品/成品</label>
            <div className="radio-group">
              <label>
                <input type="radio" checked={formData.fd120_24 === 1} disabled data-contract-field="fd120_24" />
                原料
              </label>
              <label>
                <input type="radio" checked={formData.fd120_24 === 2} disabled data-contract-field="fd120_24" />
                半成品
              </label>
              <label>
                <input type="radio" checked={formData.fd120_24 === 3} disabled data-contract-field="fd120_24" />
                成品
              </label>
            </div>

            <label>三聯式發票</label>
            <div className="checkbox-field">
              <input
                type="checkbox"
                checked={!!formData.fd120_45}
                disabled data-contract-field="fd120_45"
              />
            </div>
          </div>
        </div>

        {/* Section 3: 時間/其他 */}
        <div className="form-section odd">
          <span className="section-tag">入布/驗貨</span>
          <div className="form-grid">
            <label>入布時間</label>
            <input
              type="text"
              value={String(getField('fd120_35') || '')}
              onChange={e => setField('fd120_35', e.target.value)}
              disabled={isFieldDisabled('fd120_35')}
              style={{ width: 150 }}
              maxLength={30} data-contract-field="fd120_35"
            />

            <label>預計驗貨日期</label>
            <input
              type="date"
              value={dateToInput(formData.fd120_36)}
              onChange={e => setField('fd120_36', inputToDate(e.target.value) as FD120[keyof FD120])}
              disabled={isFieldDisabled('fd120_36')}
              style={{ width: 150 }} data-contract-field="fd120_36"
            />

            <label>預計驗貨時間</label>
            <input
              type="text"
              value={String(getField('fd120_37') || '')}
              onChange={e => setField('fd120_37', e.target.value)}
              disabled={isFieldDisabled('fd120_37')}
              style={{ width: 100 }}
              maxLength={10} data-contract-field="fd120_37"
            />

            <label>輸單人員</label>
            <div className="lookup-field">
              <input
                type="text"
                value={String(getField('fd120_91') || '')}
                disabled
                readOnly
                style={{ width: 80 }} data-contract-field="fd120_91"
              />
              <span className="lookup-name">{names.entryPerson || ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Summary Card */}
      <div className="summary-card">
        <div className="summary-title">金額摘要</div>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">繳庫類別</span>
            <span className="value">{getCategoryLabel(formData.fd120_13)}</span>
          </div>
          <div className="summary-item">
            <span className="label">原料/半成品/成品</span>
            <span className="value">{getMaterialTypeLabel(formData.fd120_24)}</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item">
            <span className="label">稅額</span>
            <span className="value">{fmt(formData.fd120_16)}</span>
          </div>
          <div className="summary-item">
            <span className="label">原幣未稅繳庫淨額</span>
            <span className="value">{fmt(formData.fd120_20)}</span>
          </div>
          <div className="summary-item total">
            <span className="label">原幣含稅繳庫總額</span>
            <span className="value">{fmt(formData.fd120_201)}</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item">
            <span className="label">總項數</span>
            <span className="value">{formData.fd120_32 || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(D202MasterFields);
